// frontend/src/components/admin/AdminNavbar.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FiBell, FiSearch, FiMenu, FiPackage, FiUser, FiSettings, FiLogOut, FiTrash2 } from 'react-icons/fi';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';

  const AdminNavbar = ({ user, onToggleSidebar, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const navItems = [
    { path: '/admin/dashboard', label: 'Хянах самбар' },
    { path: '/admin/pos', label: 'Борлуулалт (POS)' },
    { path: '/admin/products', label: 'Бараа бүтээгдэхүүн' },
    { path: '/admin/categories', label: 'Ангилал' },
    { path: '/admin/inventory', label: 'Агуулах' },
    { path: '/admin/orders', label: 'Захиалга' },
    { path: '/admin/customers', label: 'Харилцагч' },
    { path: '/admin/bank-accounts', label: 'Банкны данс' },
    { path: '/admin/reports', label: 'Тайлан' },
    { path: '/admin/users', label: 'Хэрэглэгч' },
    { path: '/admin/settings', label: 'Тохиргоо' }
  ];

  const currentPage = navItems.find(item => item.path === location.pathname)?.label || 'Админ';

  useEffect(() => {
    fetchNotifications();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/api/notifications?limit=10');
      if (response.data.success) {
        setNotifications(response.data.data.notifications);
        setUnreadCount(response.data.data.unread_count);
      }
    } catch (error) {
      console.error('Fetch notifications error:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      await axios.put(`/api/notifications/${notification.id}/read`);
      
      // Navigate to order detail if it's an order notification
      if (notification.type === 'new_order' && notification.reference_id) {
        navigate(`/admin/orders/${notification.reference_id}`);
      }
      
      setShowNotifications(false);
      fetchNotifications();
    } catch (error) {
      console.error('Mark notification as read error:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.put('/api/notifications/read-all');
      toast.success('Бүх мэдэгдэл уншсан болгов');
      fetchNotifications();
    } catch (error) {
      console.error('Mark all as read error:', error);
      toast.error('Алдаа гарлаа');
    }
  };

  // ✅ ШИНЭ - Clear read notifications
  const clearReadNotifications = async () => {
    if (!window.confirm('Бүх уншсан мэдэгдлийг устгах уу?')) {
      return;
    }

    setIsClearing(true);
    try {
      const response = await axios.delete('/api/notifications/clear');
      
      if (response.data.success) {
        toast.success(response.data.message || 'Мэдэгдэл цэвэрлэгдлээ');
        // Remove read notifications from state
        setNotifications(prev => prev.filter(n => !n.is_read));
        fetchNotifications();
      }
    } catch (error) {
      console.error('Clear notifications error:', error);
      toast.error('Мэдэгдэл устгахад алдаа гарлаа');
    } finally {
      setIsClearing(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_order':
        return '📦';
      case 'low_stock':
        return '⚠️';
      case 'order_status_change':
        return '🔄';
      default:
        return '🔔';
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'Дөнгөж сая';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} минутын өмнө`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} цагийн өмнө`;
    return `${Math.floor(seconds / 86400)} өдрийн өмнө`;
  };

  return (
    <div className="admin-navbar">
      {/* Mobile Menu Button */}
      <button
        onClick={onToggleSidebar}
        className="lg:hidden text-gray-600 hover:text-gray-900 p-2 -ml-2"
        aria-label="Toggle menu"
      >
        <FiMenu size={24} />
      </button>

      {/* Logo - Desktop */}
      <div className="hidden lg:flex items-center">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
          <FiPackage className="text-white" size={24} />
        </div>
        <div className="ml-3">
          <h2 className="text-lg font-bold text-gray-900">Бөөний систем</h2>
          <p className="text-xs text-gray-500">Админ панел</p>
        </div>
      </div>

      {/* Mobile Page Title */}
      <div className="lg:hidden flex-1 ml-3">
        <h1 className="text-lg font-semibold text-gray-900 truncate">
          {currentPage}
        </h1>
      </div>

      {/* Desktop Search */}
      <div className="navbar-search hidden lg:block">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="text-gray-400" size={18} />
          </div>
          <input
            type="text"
            placeholder="Хайх..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="navbar-actions">
        {/* Mobile Search Icon */}
        <button 
          className="lg:hidden p-2 text-gray-600 hover:text-gray-900" 
          aria-label="Search"
        >
          <FiSearch size={20} />
        </button>

        {/* Notifications */}
        <div className="relative">
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="navbar-notification" 
            aria-label="Notifications"
          >
            <FiBell size={20} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowNotifications(false)}
              ></div>
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Мэдэгдэл</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Бүгдийг унших
                    </button>
                  )}
                </div>

                {/* Notifications List */}
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <FiBell size={48} className="mx-auto mb-3 opacity-50" />
                      <p>Мэдэгдэл байхгүй</p>
                    </div>
                  ) : (
                    notifications.map(notification => (
                      <button
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                          !notification.is_read ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-2xl flex-shrink-0">
                            {getNotificationIcon(notification.type)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {getTimeAgo(notification.created_at)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Footer Actions */}
                {notifications.length > 0 && (
                  <div className="border-t border-gray-200">
                    {/* ✅ ШИНЭ - Clear Read Notifications Button */}
                    {notifications.some(n => n.is_read) && (
                      <button
                        onClick={clearReadNotifications}
                        disabled={isClearing}
                        className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-center border-b border-gray-200 flex items-center justify-center gap-2 transition-colors"
                      >
                        {isClearing ? (
                          <>
                            <div className="spinner-sm"></div>
                            <span>Устгаж байна...</span>
                          </>
                        ) : (
                          <>
                            <FiTrash2 size={14} />
                            <span>Уншсаныг цэвэрлэх</span>
                          </>
                        )}
                      </button>
                    )}

                    {/* View All Link */}
                    <div className="px-4 py-2 text-center">
                      <Link
                        to="/admin/notifications"
                        className="text-sm text-blue-600 hover:text-blue-700"
                        onClick={() => setShowNotifications(false)}
                      >
                        Бүх мэдэгдэл харах
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="hidden lg:flex items-center ml-4 hover:bg-gray-100 rounded-lg p-2 transition-colors"
          >
            <div className="text-right mr-3">
              <p className="text-sm font-semibold text-gray-900">{user?.full_name}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role_name}</p>
            </div>
            <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-700 font-semibold text-sm">
                {user?.full_name?.charAt(0).toUpperCase()}
              </span>
            </div>
          </button>

          {/* Profile Menu */}
          {showProfileMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowProfileMenu(false)}
              ></div>
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="font-semibold text-gray-900">{user?.full_name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                
                <Link
                  to="/admin/profile"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowProfileMenu(false)}
                >
                  <FiUser size={16} />
                  Хувийн мэдээлэл
                </Link>
                
                <Link
                  to="/admin/settings"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowProfileMenu(false)}
                >
                  <FiSettings size={16} />
                  Тохиргоо
                </Link>
                
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                >
                  <FiLogOut size={16} />
                  Гарах
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNavbar;